import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SingleBillPaymentMethodsManager } from '@ctct/application-layer';
import type { SingleBillPaymentMethodsHooks, SingleBillPaymentMethodsPageStore } from '@ctct/state-client-layer';
import type { PaymentStrategyQuery } from '@ctct/state-server-layer';
import {
  useEnableAmexIrAffiliate,
  usePartnerName,
  useSingleBill,
  useSingleBillSetupElement,
} from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';

interface Props {
  initializer: Pick<SingleBillPaymentMethodsManager, 'singleBillPaymentMethodsInit'>;
  pageStore: SingleBillPaymentMethodsPageStore;
  hooks: SingleBillPaymentMethodsHooks;
  paymentStrategy: PaymentStrategyQuery;
  flags?: {
    /**
     * Ignore rating call when calculating pending state?
     *
     * This is useful for pages where we don't make a rating call.
     */
    shouldIgnoreRatingCall?: boolean;
  };
}

export const useSingleBillPaymentMethodsWidget = ({ initializer, pageStore, hooks, paymentStrategy, flags }: Props) => {
  const { t } = useTranslation();
  const singleBillQuery = useSingleBill();
  const parnerNameQuery = usePartnerName();
  const isAmexIrAffiliateQuery = useEnableAmexIrAffiliate();
  const selectedBillingCountry = hooks.useSelectedBillingCountry();
  const selectedBillingCurrency = hooks.useSelectedBillingCurrency();

  const singleBillSetupElementQuery = useSingleBillSetupElement(
    paymentStrategy,
    selectedBillingCountry,
    selectedBillingCurrency
  );

  const shouldIgnoreRatingCall = flags?.shouldIgnoreRatingCall ?? false;

  const { lifecycle, ...componentProps } = useMemo(() => {
    const returnValue = initializer.singleBillPaymentMethodsInit({
      pageStore,
      queries: [singleBillQuery, parnerNameQuery, isAmexIrAffiliateQuery, singleBillSetupElementQuery],
      t,
      paymentStrategy: {
        isPending: paymentStrategy.isPending,
        data: paymentStrategy.data,
      } as PaymentStrategyQuery,
      flags: {
        shouldIgnoreRatingCall,
      },
    });

    return {
      ...returnValue,
      hooks,
    };
  }, [
    hooks,
    initializer,
    isAmexIrAffiliateQuery,
    pageStore,
    parnerNameQuery,
    paymentStrategy.isPending,
    paymentStrategy.data,
    shouldIgnoreRatingCall,
    singleBillQuery,
    singleBillSetupElementQuery,
    t,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle);

  return componentProps;
};

export type SingleBillPaymentMethodsWidgetProps = ReturnType<typeof useSingleBillPaymentMethodsWidget>;
